import { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { apiUserFetch, sharedPrefsEnabled, getSharedPrefs, saveSharedPrefs } from '../services/apiClient';

const SUPPORTED = (import.meta.env.VITE_SUPPORTED_LOCALES ?? 'en,es,de,fr')
    .split(',')
    .map((s: string) => s.trim());

interface LocaleResponse {
    locale: string | null;
}

/**
 * useLocale — read + change the active i18n locale.
 *
 * Preference scope is per-USER, NOT per-org — language choice follows the
 * user across every org. So this hook does NOT take an orgId.
 *
 * Persistence (active in BOTH standalone + IGNODE modes):
 *   - i18next-browser-languagedetector caches the selection in localStorage
 *     under key `ignode-annotator-locale` (configured in i18n/index.ts)
 *   - backend POST /api/v1/users/<userId>/preferences/locale roams the
 *     choice across browsers/devices for the same user. Best-effort:
 *     server unreachable doesn't block the UI.
 */
export function useLocale() {
    const { i18n } = useTranslation();

    const supported = SUPPORTED;
    const current = (i18n.resolvedLanguage || i18n.language || 'en').split('-')[0];

    // Pull server-side value on first mount.
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                if (sharedPrefsEnabled) {
                    // IGNODE mode — read from the shared cross-surface store.
                    const body = await getSharedPrefs();
                    if (cancelled) return;
                    const lng = body.locale;
                    if (lng && lng !== current && supported.includes(lng)) {
                        await i18n.changeLanguage(lng);
                    }
                    return;
                }
                const body = await apiUserFetch<LocaleResponse>('/preferences/locale');
                if (cancelled || !body.locale) return;
                if (body.locale !== current && supported.includes(body.locale)) {
                    await i18n.changeLanguage(body.locale);
                }
            } catch { /* swallow */ }
        })();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const setLocale = useCallback(
        async (next: string) => {
            if (!supported.includes(next)) return;
            await i18n.changeLanguage(next);
            if (sharedPrefsEnabled) {
                // IGNODE mode — write to the shared cross-surface store. The
                // store's language key is `locale` (users-api + FF convention),
                // same name the annotator uses internally — no mapping needed.
                saveSharedPrefs({ locale: next }).catch(() => { /* swallow */ });
            } else {
                apiUserFetch('/preferences/locale', undefined, {
                    method: 'PUT',
                    body: { locale: next },
                }).catch(() => { /* swallow */ });
            }
        },
        [i18n, supported],
    );

    return { locale: current, setLocale, supported };
}
